import { useState } from "react";
import { BfrsProvider } from "@/components/ui/providers/BfrsProvider";
import { Button } from "@/components/primitives/Button";
import { Badge } from "@/components/primitives/Badge";
import { Input } from "@/components/primitives/Input";
import { Chip } from "@/components/primitives/Chip";
import { Select } from "@/components/ui/forms/Select";
import { Stack } from "@/components/ui/primitives/Stack";

const POISON_STYLE: React.CSSProperties = {
  color: "red",
  fontFamily: "Georgia, 'Times New Roman', serif",
  fontSize: "22px",
  fontWeight: "900",
  lineHeight: "3",
  letterSpacing: "0.2em",
  textTransform: "uppercase",
  background: "linear-gradient(135deg, #ffe066 0%, #ff6b6b 100%)",
  padding: "24px",
  borderRadius: "12px",
};

function ComponentSet({ label }: { label: string }) {
  const [value, setValue] = useState("apple");
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      <p style={{ margin: 0, fontSize: "13px", fontFamily: "monospace", fontWeight: "normal", lineHeight: "1.4", color: "#333", textTransform: "none", letterSpacing: "normal" }}>
        {label}
      </p>
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
        <Button variant="primary" size="sm">Primary</Button>
        <Button variant="secondary" size="sm">Secondary</Button>
        <Button variant="destructive" size="sm">Destructive</Button>
      </div>
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
        <Badge tone="success">Verified</Badge>
        <Badge tone="warning">Pending</Badge>
        <Badge tone="danger">Failed</Badge>
        <Chip>Chip label</Chip>
      </div>
      <Input placeholder="Type something…" style={{ maxWidth: "280px" }} />
      <div style={{ maxWidth: "280px" }}>
        <Select
          value={value}
          onValueChange={setValue}
          options={[
            { value: "apple", label: "Apple" },
            { value: "banana", label: "Banana" },
            { value: "cherry", label: "Cherry" },
          ]}
        />
      </div>
    </div>
  );
}

export function IsolationTestPage() {
  return (
    <div style={{ padding: "32px", fontFamily: "system-ui, sans-serif", background: "#f5f5f5", minHeight: "100vh" }}>
      <h1 style={{ fontSize: "20px", fontWeight: "700", marginBottom: "8px", color: "#111" }}>CSS Isolation Test</h1>
      <p style={{ fontSize: "14px", color: "#555", marginBottom: "32px" }}>
        Both sections below sit inside an aggressively styled container (red text, serif font, huge letter-spacing, gradient background).
        Components inside <code>BfrsProvider</code> should be completely unaffected.
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px" }}>
        {/* WITHOUT BfrsProvider */}
        <div>
          <div style={{ fontSize: "13px", fontWeight: "600", marginBottom: "8px", color: "#c00", textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Without BfrsProvider — styles leak in
          </div>
          <div style={POISON_STYLE}>
            <ComponentSet label="No isolation boundary — host styles cascade into components" />
          </div>
        </div>

        {/* WITH BfrsProvider */}
        <div>
          <div style={{ fontSize: "13px", fontWeight: "600", marginBottom: "8px", color: "#1a7f37", textTransform: "uppercase", letterSpacing: "0.05em" }}>
            With BfrsProvider — styles blocked at boundary
          </div>
          <div style={POISON_STYLE}>
            <BfrsProvider>
              <ComponentSet label="all: initial on BfrsProvider div — host styles cannot cascade through" />
            </BfrsProvider>
          </div>
        </div>
      </div>

      {/* Width/position/display passthrough proof */}
      <div style={{ marginTop: "40px" }}>
        <h2 style={{ fontSize: "16px", fontWeight: "700", marginBottom: "8px", color: "#111" }}>Passthrough: width, position, display</h2>
        <p style={{ fontSize: "13px", color: "#555", marginBottom: "16px" }}>
          Even with isolation, <code>width</code>, <code>position</code>, and <code>display</code> on the BfrsProvider element itself remain overridable.
        </p>
        <Stack gap={3}>
          <BfrsProvider style={{ width: "320px", border: "2px dashed #0070f3", borderRadius: "8px", padding: "12px" }}>
            <Stack gap={2}>
              <p style={{ margin: 0, fontSize: "12px", color: "#0070f3", fontFamily: "monospace" }}>width: 320px via style prop</p>
              <Button variant="primary">Constrained width button</Button>
              <Input placeholder="Constrained width input" />
            </Stack>
          </BfrsProvider>

          <BfrsProvider style={{ display: "inline-flex", border: "2px dashed #8b5cf6", borderRadius: "8px", padding: "12px" }}>
            <Stack gap={2} direction="row">
              <p style={{ margin: 0, fontSize: "12px", color: "#8b5cf6", fontFamily: "monospace" }}>display: inline-flex via style prop</p>
              <Button variant="secondary" size="sm">Inline</Button>
              <Badge tone="info">badge</Badge>
            </Stack>
          </BfrsProvider>
        </Stack>
      </div>
    </div>
  );
}
